import React, { useEffect, useMemo, useState } from "react";

// Lazy zxcvbn init so the bundle can code-split it out of critical login path.
let _optionsLoaded = false;
async function ensureZxcvbn() {
  const [{ zxcvbnOptions, zxcvbn }, common, en] = await Promise.all([
    import("@zxcvbn-ts/core"),
    import("@zxcvbn-ts/language-common"),
    import("@zxcvbn-ts/language-en"),
  ]);
  if (!_optionsLoaded) {
    zxcvbnOptions.setOptions({
      translations: en.translations,
      graphs: common.adjacencyGraphs,
      dictionary: { ...common.dictionary, ...en.dictionary },
    });
    _optionsLoaded = true;
  }
  return zxcvbn;
}

const LABELS = ["Very weak", "Weak", "Fair", "Strong", "Very strong"];
const COLORS = ["#c62839", "#e57f22", "#f0a91d", "#7ac74f", "#0a8754"];

export const MIN_PASSWORD_LENGTH = 8;

/**
 * Password strength meter. Shows a 4-segment bar, label, and (optional) tips.
 * Fires onChange({ score, valid }) so the parent form can gate the submit.
 */
export function PasswordStrengthMeter({ password = "", onChange, userInputs = [], showLabel = true }) {
  const [result, setResult] = useState({ score: 0, feedback: { warning: "", suggestions: [] } });

  useEffect(() => {
    let cancelled = false;
    if (!password) { setResult({ score: 0, feedback: { warning: "", suggestions: [] } }); return; }
    ensureZxcvbn().then((zxcvbn) => {
      if (cancelled) return;
      const r = zxcvbn(password, userInputs);
      setResult({ score: r.score, feedback: r.feedback || { warning: "", suggestions: [] } });
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [password, JSON.stringify(userInputs)]);

  const valid = password.length >= MIN_PASSWORD_LENGTH && result.score >= 2;
  useEffect(() => { onChange && onChange({ score: result.score, valid }); /* eslint-disable-next-line */ }, [result.score, valid]);

  const segments = useMemo(() => Array.from({ length: 4 }, (_, i) => i < result.score), [result.score]);
  const barColor = COLORS[Math.max(0, Math.min(result.score, COLORS.length - 1))];

  if (!password) return null;
  return (
    <div className="mt-2" data-testid="password-strength">
      <div className="flex gap-1">
        {segments.map((filled, i) => (
          <div key={i} className="h-1.5 flex-1 rounded-full transition-colors" style={{ background: filled ? barColor : "var(--surface-2)" }} />
        ))}
      </div>
      {showLabel && (
        <div className="mt-1 flex items-center justify-between text-[11px]">
          <span data-testid="password-strength-label" className="font-medium" style={{ color: barColor }}>{LABELS[result.score]}</span>
          <span className="text-[color:var(--ink-3)]">
            {password.length < MIN_PASSWORD_LENGTH ? `${MIN_PASSWORD_LENGTH - password.length} more char${MIN_PASSWORD_LENGTH - password.length === 1 ? "" : "s"}` :
              result.score < 2 ? "Add a symbol or mix of case to strengthen" : "Meets policy"}
          </span>
        </div>
      )}
      {result.feedback?.warning && result.score < 2 && (
        <p className="text-[11px] text-[color:var(--danger)] mt-1">{result.feedback.warning}</p>
      )}
    </div>
  );
}
